import {
    createFailure,
    createValidationResult,
    FailureSeverity,
    ValidationFailure,
    ValidationResult
} from "./ValidationResult";

export type ValidationCondition<T> = (obj: T) => boolean;

export interface FluentValidator<T> {
    when(condition: ValidationCondition<T>): FluentRuleBuilder<T>;
    validate(obj: T): ValidationResult;
}

type Message<T> = string | ((item: T) => string);

interface FluentRuleBuilder<T> {
    addWarning(message: Message<T>, code?: string): FluentValidator<T>;
    addError(message: Message<T>, code?: string): FluentValidator<T>;
}

interface FluentValidationRule<T> extends FluentRuleBuilder<T> {
    execute(obj: T): ValidationFailure | undefined;
}

const whenFluentValidationRule = <T>(
    currentValidator: FluentValidator<T>,
    conditionToCheck: ValidationCondition<T>
): FluentValidationRule<T> => {
    const ruleToExecute: ValidationCondition<T> = conditionToCheck;

    let ruleMessage: Message<T>;
    let ruleCode = ``;
    let ruleSeverity: FailureSeverity;

    const addWarning = (message: Message<T>, code?: string): FluentValidator<T> => {
        ruleMessage = message;
        if (code) ruleCode = code;
        ruleSeverity = FailureSeverity.Warning;

        return currentValidator;
    };

    const addError = (message: string, code?: string): FluentValidator<T> => {
        ruleMessage = message;
        if (code) ruleCode = code;
        ruleSeverity = FailureSeverity.Error;
        return currentValidator;
    };

    const execute = (obj: T): ValidationFailure | undefined => {
        const ruleResult = ruleToExecute(obj);

        if (ruleResult) {
            let message = "";

            if (typeof ruleMessage !== "string") {
                message = ruleMessage(obj);
            } else {
                message = ruleMessage;
            }

            return createFailure(message, ruleCode, ruleSeverity);
        }
        return undefined;
    };

    return {
        addWarning,
        addError,
        execute
    };
};

export const fluentValidator = <T>(): FluentValidator<T> => {
    const rules: FluentValidationRule<T>[] = [];

    const when = (ruleToVerify: ValidationCondition<T>): FluentRuleBuilder<T> => {
        const rule = whenFluentValidationRule(validator, ruleToVerify);
        rules.push(rule);
        return rule;
    };

    const validate = (obj: T): ValidationResult => {
        const failures: ValidationFailure[] = [];
        rules.forEach((rule) => {
            const result = rule.execute(obj);
            if (result) {
                failures.push(result);
            }
        });

        return createValidationResult(failures);
    };

    const validator = {
        when,
        validate
    };

    return validator;
};
